import PropTypes from 'prop-types';
import React from 'react';

import {VM} from '@hetao/scratch-core';

import Box from '../box/box.jsx';
import SpriteList from '../sprite-selector/sprite-list.jsx';
import {STAGE_DISPLAY_SIZES} from '../../lib/layout-constants';
import SpriteLibrary from '../../containers/sprite-library.jsx';
import AssetLibrary from '../asset-modal/asset-modal.jsx';
import PaintEditor from '../../containers/paint-editor-modal.jsx';

import styles from './target-pane-ht.css';

/*
 * Pane that contains the sprite selector, sprite info, stage selector,
 * and the new sprite, costume and backdrop buttons
 * @param {object} props Props for the component
 * @returns {React.Component} rendered component
 */
const TargetPaneHt = ({
    editingTarget,
    hoveredTarget,
    spriteLibraryVisible,
    assetModalVisible,
    paintEditorVisible,
    onConfigSpriteClick,
    onDeleteSprite,
    onDrop,
    onDuplicateSprite,
    onExportSprite,
    onNewSpriteClick,
    onRequestCloseSpriteLibrary,
    onRequestCloseAsset,
    onRequestClosePaintEditor,
    onSelectSprite,
    raiseSprites,
    stage,
    stageSize,
    targets,
    vm,
    ...componentProps
}) => (
    <div
        className={styles.targetPane}
        {...componentProps}
    >
        <Box className={styles.scrollWrapper}>
            <SpriteList
                editingTarget={editingTarget}
                hoveredTarget={hoveredTarget}
                items={targets}
                raised={raiseSprites}
                selectedId={editingTarget}
                stage={stage}
                stageSize={stageSize}
                onConfigSpriteClick={onConfigSpriteClick}
                onDeleteSprite={onDeleteSprite}
                onDrop={onDrop}
                onDuplicateSprite={onDuplicateSprite}
                onExportSprite={onExportSprite}
                onNewSpriteClick={onNewSpriteClick}
                onSelectSprite={onSelectSprite}
            />
        </Box>
        {spriteLibraryVisible ? (
            <SpriteLibrary
                vm={vm}
                onRequestClose={onRequestCloseSpriteLibrary}
            />
        ) : null}
        {assetModalVisible ?
            <AssetLibrary
                vm={vm}
                onRequestClose={onRequestCloseAsset}
            /> : null
        }
        {paintEditorVisible ?
            <PaintEditor
                onRequestClose={onRequestClosePaintEditor}
            /> : null
        }
    </div>
);

const spriteShape = PropTypes.shape({
    costume: PropTypes.shape({
        url: PropTypes.string,
        name: PropTypes.string.isRequired,
        // The following are optional because costumes uploaded from disk
        // will not have these properties available
        bitmapResolution: PropTypes.number,
        rotationCenterX: PropTypes.number,
        rotationCenterY: PropTypes.number
    }),
    direction: PropTypes.number,
    id: PropTypes.string,
    name: PropTypes.string,
    order: PropTypes.number,
    size: PropTypes.number,
    visibility: PropTypes.bool,
    x: PropTypes.number,
    y: PropTypes.number
});

TargetPaneHt.propTypes = {
    assetModalVisible: PropTypes.bool,
    backdropLibraryVisible: PropTypes.bool,
    editingTarget: PropTypes.string,
    extensionLibraryVisible: PropTypes.bool,
    fileInputRef: PropTypes.func,
    handleNewBlankCostume: PropTypes.func,
    hoveredTarget: PropTypes.shape({
        hoveredSprite: PropTypes.string,
        receivedBlocks: PropTypes.bool
    }),
    onChangeSpriteDirection: PropTypes.func,
    onChangeSpriteName: PropTypes.func,
    onChangeSpriteSize: PropTypes.func,
    onChangeSpriteVisibility: PropTypes.func,
    onChangeSpriteX: PropTypes.func,
    onChangeSpriteY: PropTypes.func,
    onConfigSpriteClick: PropTypes.func,
    onDeleteSprite: PropTypes.func,
    onDrop: PropTypes.func,
    onDuplicateSprite: PropTypes.func,
    onExportSprite: PropTypes.func,
    onFileUploadClick: PropTypes.func,
    onNewSpriteClick: PropTypes.func,
    onRequestCloseAsset: PropTypes.func,
    onRequestCloseExtensionLibrary: PropTypes.func,
    onRequestClosePaintEditor: PropTypes.func,
    onRequestCloseSpriteLibrary: PropTypes.func,
    onSelectSprite: PropTypes.func,
    onSpriteUpload: PropTypes.func,
    paintEditorVisible: PropTypes.bool,
    raiseSprites: PropTypes.bool,
    spriteLibraryVisible: PropTypes.bool,
    stage: spriteShape,
    stageSize: PropTypes.oneOf(Object.keys(STAGE_DISPLAY_SIZES)).isRequired,
    targets: PropTypes.objectOf(spriteShape),
    vm: PropTypes.instanceOf(VM)
};

export default TargetPaneHt;
